import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthProvider';
import { useTheme } from '../lib/useTheme';
import { useScreen } from '../lib/useScreen';
import { supabase } from '../lib/supabaseClient';
import UserAvatar from '../lib/UserAvatar';
import ClientWorkspace from '../components/admin/ClientWorkspace';
import ClientCard from '../components/admin/ClientCard';
import AdminViewSwitcher from '../lib/AdminViewSwitcher';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', logoGrad:'linear-gradient(135deg, #534AB7, #1D9E75)', triGrad:'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b)' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const TIER_PRICES = { doer:139, spark_seeker:139, momentum_builder:27, growth_mastermind:44, clarity_mentor:66, onsite_mexico:100 };
const TIER_LABELS = { free_observer:'Free Observer', doer:'Doer', spark_seeker:'Doer', momentum_builder:'Momentum', growth_mastermind:'Growth', clarity_mentor:'Clarity', onsite_mexico:'Onsite', admin:'Admin' };
const TIER_COLORS = { free_observer:'#999', doer:C.mango, spark_seeker:C.mango, momentum_builder:C.turquesa, growth_mastermind:C.coral, onsite_mexico:'#EF9F27', admin:'#534AB7' };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, profileLoaded, signOut, setViewAs } = useAuth();
  const t = useTheme();
  const { isMobile } = useScreen();
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [delForm, setDelForm] = useState({ title:'', type:'documento', status:'pending', delivery_date:'', link_url:'', notes:'' });
  const [newsletters, setNewsletters] = useState([]);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [pageViews, setPageViews] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [evtForm, setEvtForm] = useState({ title:'', event_date:'', event_type:'general', meet_link:'', description:'' });
  const [evtSaving, setEvtSaving] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [pushTipo, setPushTipo] = useState('noticias');
  const [pushTitle, setPushTitle] = useState('Nuevas noticias');
  const [pushBody, setPushBody] = useState('Hay novedades en la comunidad. Entra a ver!');
  const [pushDest, setPushDest] = useState('all');
  const [pushSending, setPushSending] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [showMonthlyReport, setShowMonthlyReport] = useState(false);
  const [onsiteClient, setOnsiteClient] = useState(null);
  const [sharedTaskCounts, setSharedTaskCounts] = useState({});
  const [reportAI, setReportAI] = useState(null);
  const [reportAILoading, setReportAILoading] = useState(false);

  useEffect(() => {
    if (!profileLoaded) return;
    if (!isAdmin) { navigate('/sala'); return; }
    loadUsers();
  }, [profileLoaded, isAdmin]);

  const loadUsers = async () => {
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    if (data) setUsers(data);
  };

  const loadDeliverables = async (uid) => {
    const { data } = await supabase.from('deliverables').select('*').eq('user_id', uid).order('created_at', { ascending: false });
    if (data) setDeliverables(data);
  };

  const loadNewsletters = async () => {
    const { data } = await supabase.from('newsletters').select('*').order('created_at', { ascending: false }).limit(20);
    if (data) setNewsletters(data);
  };

  const loadChat = async () => {
    const { data } = await supabase.from('community_chat').select('*').order('created_at', { ascending: false }).limit(50);
    if (data) setChatMsgs(data);
  };

  const loadPageViews = async () => {
    const { data } = await supabase.from('page_views').select('*').order('created_at', { ascending: false }).limit(500);
    if (data) setPageViews(data);
  };

  const loadEvents = async () => {
    const { data } = await supabase.from('events').select('*').order('event_date', { ascending: false });
    if (data) setAllEvents(data);
  };

  useEffect(() => {
    if (tab === 'newsletter') loadNewsletters();
    if (tab === 'chat') loadChat();
    if (tab === 'analytics') loadPageViews();
    if (tab === 'events') loadEvents();
    if (tab === 'feedback') supabase.from('feedback').select('*').order('created_at',{ascending:false}).then(({data})=>{if(data)setFeedbackList(data)});
    if (tab === 'onsite') supabase.from('shared_tasks').select('client_id, status').eq('status','pending').then(({data})=>{
      if (data) { const counts={}; data.forEach(t=>{counts[t.client_id]=(counts[t.client_id]||0)+1}); setSharedTaskCounts(counts); }
    });
  }, [tab]);

  useEffect(() => {
    if (selectedUser) loadDeliverables(selectedUser.id);
  }, [selectedUser]);

  // Monthly report check
  useEffect(() => {
    if (!user || users.length === 0) return;
    const now = new Date();
    if (now.getDate() <= 3) {
      const key = `td_admin_report_${now.getFullYear()}_${now.getMonth()}`;
      if (!localStorage.getItem(key)) setShowMonthlyReport(true);
    }
  }, [user, users]);

  // KPI calculations
  const totalUsers = users.length;
  const paidUsers = users.filter(u => u.membership && u.membership !== 'free_observer' && u.membership !== 'admin');
  const mrr = paidUsers.reduce((s, u) => s + (TIER_PRICES[u.membership] || 0), 0);
  const convRate = totalUsers > 0 ? Math.round((paidUsers.length / totalUsers) * 100) : 0;
  const tierCounts = {};
  users.forEach(u => { const m = u.membership || 'free_observer'; tierCounts[m] = (tierCounts[m] || 0) + 1; });
  const onsiteUsers = users.filter(u => u.membership === 'onsite_mexico');
  const filteredUsers = filter === 'all' ? users : users.filter(u => (u.membership || 'free_observer') === filter);

  const changeMembership = async (uid, newMembership) => {
    await supabase.from('profiles').update({ membership: newMembership }).eq('id', uid);
    loadUsers();
    if (selectedUser?.id === uid) setSelectedUser(prev => ({ ...prev, membership: newMembership }));
  };

  const addDeliverable = async () => {
    if (!selectedUser || !delForm.title.trim()) return;
    setSaving(true);
    await supabase.from('deliverables').insert({ ...delForm, user_id: selectedUser.id });
    setDelForm({ title:'', type:'documento', status:'pending', delivery_date:'', link_url:'', notes:'' });
    loadDeliverables(selectedUser.id);
    setSaving(false);
  };

  const updateDelStatus = async (id, status) => {
    await supabase.from('deliverables').update({ status }).eq('id', id);
    if (selectedUser) loadDeliverables(selectedUser.id);
  };

  const deleteDeliverable = async (id) => {
    await supabase.from('deliverables').delete().eq('id', id);
    if (selectedUser) loadDeliverables(selectedUser.id);
  };

  const deleteNewsItem = async (id) => {
    await supabase.from('newsletters').delete().eq('id', id);
    loadNewsletters();
  };

  const deleteChatMsg = async (id) => {
    await supabase.from('community_chat').delete().eq('id', id);
    loadChat();
  };

  const generateNewsletter = async () => {
    setGenLoading(true);
    try {
      const r = await fetch('/api/newsletter-agent');
      const d = await r.json();
      if (d.success) { loadNewsletters(); alert('Newsletter generado: ' + (d.articles?.length || 0) + ' artículos'); }
      else alert('Error: ' + (d.error || 'desconocido'));
    } catch (e) { alert('Error: ' + e.message); }
    setGenLoading(false);
  };

  const inp = { width:'100%', padding:'10px 14px', border:`0.5px solid ${t.border}`, borderRadius:12, fontSize:14, fontFamily:font, background:t.card, color:t.text, outline:'none', boxSizing:'border-box' };
  const statColor = { entregado:C.turquesa, proceso:C.mango, pending:'#999' };
  const statLabel = { entregado:'Entregado', proceso:'En proceso', pending:'Pendiente' };

  if (!profileLoaded) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:font }}><p style={{ color:'#999' }}>Cargando...</p></div>;
  if (!isAdmin) return null;

  const TABS = [
    { id:'overview', label:'Overview', emoji:'📊' },
    { id:'users', label:'Usuarios', emoji:'👥' },
    { id:'onsite', label:'Onsite', emoji:'🏢' },
    { id:'newsletter', label:'Newsletter', emoji:'📰' },
    { id:'chat', label:'Chat', emoji:'💬' },
    { id:'analytics', label:'Analytics', emoji:'🧠' },
    { id:'events', label:'Eventos', emoji:'📅' },
    { id:'feedback', label:'Feedback', emoji:'🐛' },
    { id:'push', label:'Push', emoji:'🔔' },
    { id:'tu_radar_admin', label:'Tu Radar', emoji:'📡' },
  ];

  return (
    <div style={{ background:t.bg, fontFamily:font, color:t.text, minHeight:'100vh' }}>
      {/* NAV */}
      <nav style={{ background:t.isDark?'#222':'#1a1a1a', padding:'0 1.5rem', height:56, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:30, height:30, borderRadius:'50%', background:C.logoGrad, display:'flex', alignItems:'center', justifyContent:'center' }}><span style={{ color:'#fff', fontSize:9, fontWeight:600 }}>T&D</span></div>
          <span style={{ color:'#fff', fontSize:15, fontWeight:500 }}>Panel Admin</span>
        </div>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <AdminViewSwitcher />
          <button onClick={()=>navigate('/sala')} style={{ background:'none', border:'none', color:'#999', fontSize:12, cursor:'pointer', fontFamily:font }}>La Sala</button>
          <button onClick={async()=>{await signOut();navigate('/')}} style={{ background:'none', border:'1px solid #444', color:'#fff', borderRadius:50, padding:'5px 14px', fontSize:12, cursor:'pointer', fontFamily:font }}>Salir</button>
        </div>
      </nav>

      {/* TABS */}
      <div style={{ borderBottom:`0.5px solid ${t.border}`, padding:'0 1.5rem', display:'flex', gap:0, overflowX:'auto' }}>
        {TABS.map(tb => (
          <button key={tb.id} onClick={()=>setTab(tb.id)} style={{ padding:'12px 16px', fontSize:13, fontWeight:tab===tb.id?600:400, color:tab===tb.id?C.turquesa:t.textSecondary, borderBottom:tab===tb.id?`2px solid ${C.turquesa}`:'2px solid transparent', background:tab===tb.id?'rgba(93,193,185,0.08)':'none', border:'none', borderBottomStyle:'solid', cursor:'pointer', fontFamily:font, whiteSpace:'nowrap', borderRadius:'8px 8px 0 0', boxShadow:tab===tb.id?'0 0 15px rgba(93,193,185,0.1)':'none', transition:'background 0.2s, box-shadow 0.2s' }}>
            {isMobile ? tb.emoji : `${tb.emoji} ${tb.label}`}
          </button>
        ))}
      </div>

      <div style={{ maxWidth:1100, margin:'0 auto', padding:isMobile?'1rem':'1.5rem' }}>

        {/* ═══ OVERVIEW ═══ */}
        {tab === 'overview' && (
          <>
            <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr 1fr':'repeat(5,1fr)', gap:12, marginBottom:28 }}>
              {[
                { label:'Usuarios', value:totalUsers, emoji:'👥' },
                { label:'De paga', value:paidUsers.length, emoji:'💳' },
                { label:'MRR', value:'$'+mrr, emoji:'💰' },
                { label:'Conversión', value:convRate+'%', emoji:'📈' },
                { label:'Churn', value:'0%', emoji:'📉' },
              ].map((k,i) => (
                <div key={i} style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:'16px', textAlign:'center' }}>
                  <p style={{ fontSize:24, margin:'0 0 4px' }}>{k.emoji}</p>
                  <p style={{ fontSize:22, fontWeight:500, margin:'0 0 2px' }}>{k.value}</p>
                  <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>{k.label}</p>
                </div>
              ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:16, marginBottom:28 }}>
              {/* Membership distribution */}
              <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.5rem' }}>
                <p style={{ fontSize:15, fontWeight:500, margin:'0 0 16px' }}>Distribución de membresías</p>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {Object.entries(tierCounts).sort((a,b) => b[1]-a[1]).map(([tier, count]) => (
                    <div key={tier} style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:12, height:12, borderRadius:4, background:TIER_COLORS[tier]||'#999', flexShrink:0 }}/>
                      <span style={{ flex:1, fontSize:13 }}>{TIER_LABELS[tier]||tier}</span>
                      <span style={{ fontSize:14, fontWeight:600 }}>{count}</span>
                      <div style={{ width:60, height:6, borderRadius:3, background:t.isDark?'#333':'#eee', overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${totalUsers>0?(count/totalUsers)*100:0}%`, background:TIER_COLORS[tier]||'#999', borderRadius:3 }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Revenue by tier */}
              <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.5rem' }}>
                <p style={{ fontSize:15, fontWeight:500, margin:'0 0 16px' }}>Ingresos por tier</p>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {Object.entries(TIER_PRICES).map(([tier, price]) => {
                    const count = tierCounts[tier] || 0;
                    return (
                      <div key={tier} style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:12, height:12, borderRadius:4, background:TIER_COLORS[tier]||'#999', flexShrink:0 }}/>
                        <span style={{ flex:1, fontSize:13 }}>{TIER_LABELS[tier]||tier} ({count})</span>
                        <span style={{ fontSize:14, fontWeight:600, color:C.turquesa }}>${count * price}</span>
                      </div>
                    );
                  })}
                  <div style={{ borderTop:`0.5px solid ${t.border}`, paddingTop:8, display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:14, fontWeight:600 }}>Total MRR</span>
                    <span style={{ fontSize:18, fontWeight:600, color:C.turquesa }}>${mrr} USD</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent users */}
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.5rem' }}>
              <p style={{ fontSize:15, fontWeight:500, margin:'0 0 12px' }}>Últimos registros</p>
              {users.slice(0, 5).map(u => (
                <div key={u.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:`0.5px solid ${t.border}` }}>
                  <UserAvatar url={u.avatar_url} name={u.full_name} size={32}/>
                  <div style={{ flex:1 }}>
                    <p style={{ fontSize:13, fontWeight:500, margin:0 }}>{u.full_name||u.email}</p>
                    <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>{u.business_name||'Sin negocio'}</p>
                  </div>
                  <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:(TIER_COLORS[u.membership]||'#999')+'18', color:TIER_COLORS[u.membership]||'#999', fontWeight:600 }}>{TIER_LABELS[u.membership]||'Free'}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ═══ USUARIOS ═══ */}
        {tab === 'users' && (
          <>
            <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
              {['all','free_observer','spark_seeker','momentum_builder','growth_mastermind','onsite_mexico'].map(f => (
                <button key={f} onClick={()=>setFilter(f)} style={{ padding:'6px 14px', fontSize:12, borderRadius:50, border:`0.5px solid ${filter===f?C.turquesa:t.border}`, background:filter===f?C.turquesa+'18':'none', color:filter===f?C.turquesa:t.textSecondary, cursor:'pointer', fontFamily:font, fontWeight:500 }}>
                  {f==='all'?'Todos':TIER_LABELS[f]||f} ({f==='all'?users.length:tierCounts[f]||0})
                </button>
              ))}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {filteredUsers.map(u => (
                <div key={u.id} onClick={()=>setSelectedUser(u)} style={{ background:t.card, border:`0.5px solid ${selectedUser?.id===u.id?C.turquesa:t.border}`, borderRadius:16, padding:'14px 18px', display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                  <UserAvatar url={u.avatar_url} name={u.full_name} size={36}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:14, fontWeight:500, margin:0 }}>{u.full_name||'Sin nombre'}</p>
                    <p style={{ fontSize:11, color:t.textSecondary, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email} · {u.business_name||'—'}</p>
                  </div>
                  <select value={u.membership||'free_observer'} onClick={e=>e.stopPropagation()} onChange={e=>changeMembership(u.id, e.target.value)} style={{ fontSize:11, padding:'4px 8px', borderRadius:8, border:`0.5px solid ${t.border}`, background:t.card, color:TIER_COLORS[u.membership]||'#999', fontWeight:600, fontFamily:font }}>
                    {Object.entries(TIER_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              ))}
            </div>
            {selectedUser && (
              <div style={{ marginTop:20, background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.5rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                  <UserAvatar url={selectedUser.avatar_url} name={selectedUser.full_name} size={50}/>
                  <div>
                    <p style={{ fontSize:18, fontWeight:500, margin:0 }}>{selectedUser.full_name}</p>
                    <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>{selectedUser.email}</p>
                    <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>{selectedUser.business_name} · {selectedUser.city}</p>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                  <div style={{ background:t.isDark?'#333':'#f8f8f6', borderRadius:12, padding:12, textAlign:'center' }}>
                    <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Membresía</p>
                    <p style={{ fontSize:14, fontWeight:600, color:TIER_COLORS[selectedUser.membership], margin:0 }}>{TIER_LABELS[selectedUser.membership]}</p>
                  </div>
                  <div style={{ background:t.isDark?'#333':'#f8f8f6', borderRadius:12, padding:12, textAlign:'center' }}>
                    <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Registro</p>
                    <p style={{ fontSize:13, fontWeight:500, margin:0 }}>{new Date(selectedUser.created_at).toLocaleDateString('es-MX')}</p>
                  </div>
                  <div style={{ background:t.isDark?'#333':'#f8f8f6', borderRadius:12, padding:12, textAlign:'center' }}>
                    <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Onboarding</p>
                    <p style={{ fontSize:13, fontWeight:500, margin:0 }}>{selectedUser.onboarding_done?'✅':'❌'}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══ ONSITE ═══ */}
        {tab === 'onsite' && (
          onsiteClient ? (
            <ClientWorkspace
              client={onsiteClient}
              adminId={user?.id}
              onBack={() => setOnsiteClient(null)}
              onViewAs={(clientId) => { const u = users.find(x => x.id === clientId); if (u) { setViewAs(u.membership); navigate('/escritorio/onsite'); } }}
            />
          ) : (
            <>
              <p style={{ fontSize:15, fontWeight:500, margin:'0 0 16px' }}>Mis Clientes Onsite ({onsiteUsers.length})</p>
              {onsiteUsers.length === 0 ? (
                <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:20 }}>No hay clientes Onsite aún</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {onsiteUsers.map(u => (
                    <ClientCard key={u.id} client={u} onClick={() => setOnsiteClient(u)} pendingCount={sharedTaskCounts[u.id] || 0} t={t} />
                  ))}
                </div>
              )}
            </>
          )
        )}

        {/* ═══ NEWSLETTER ═══ */}
        {tab === 'newsletter' && (
          <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
              <p style={{ fontSize:15, fontWeight:500, margin:0 }}>Newsletters ({newsletters.length})</p>
              <button onClick={generateNewsletter} disabled={genLoading} style={{ background:C.logoGrad, color:'#fff', border:'none', borderRadius:50, padding:'10px 24px', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:font, opacity:genLoading?0.6:1 }}>
                {genLoading?'Generando...':'🤖 Generar newsletter ahora'}
              </button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {newsletters.map(n => (
                <div key={n.id} style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:'14px 18px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontSize:14, fontWeight:500, margin:'0 0 4px' }}>{n.image_emoji} {n.title}</p>
                      <p style={{ fontSize:12, color:t.textSecondary, margin:'0 0 4px' }}>{n.preview?.substring(0,100)}...</p>
                      <p style={{ fontSize:10, color:t.textSecondary, margin:0 }}>{n.tag} · Semana {n.week_number} · {new Date(n.created_at).toLocaleDateString('es-MX')}</p>
                    </div>
                    <button onClick={()=>deleteNewsItem(n.id)} style={{ background:'none', border:'none', color:'#999', fontSize:16, cursor:'pointer', flexShrink:0 }}>×</button>
                  </div>
                </div>
              ))}
              {newsletters.length===0 && <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:20 }}>No hay newsletters</p>}
            </div>
          </>
        )}

        {/* ═══ CHAT ═══ */}
        {tab === 'chat' && (
          <>
            <p style={{ fontSize:15, fontWeight:500, margin:'0 0 16px' }}>Chat de la comunidad ({chatMsgs.length} mensajes)</p>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {chatMsgs.map(m => (
                <div key={m.id} style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:12, padding:'10px 14px', display:'flex', alignItems:'center', gap:10 }}>
                  <UserAvatar url={m.avatar_url} name={m.user_name} size={28}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:12, fontWeight:600, margin:0 }}>{m.user_name} <span style={{ fontWeight:400, color:t.textSecondary }}>{new Date(m.created_at).toLocaleString('es-MX',{hour:'2-digit',minute:'2-digit',day:'numeric',month:'short'})}</span></p>
                    <p style={{ fontSize:13, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.message}</p>
                  </div>
                  <button onClick={()=>deleteChatMsg(m.id)} style={{ background:'none', border:'none', color:'#999', fontSize:14, cursor:'pointer', flexShrink:0 }}>×</button>
                </div>
              ))}
              {chatMsgs.length===0 && <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:20 }}>No hay mensajes</p>}
            </div>
          </>
        )}

        {/* ═══ ANALYTICS ═══ */}
        {tab === 'analytics' && (() => {
          const now = new Date();
          const day1 = new Date(now); day1.setDate(day1.getDate()-1);
          const day7 = new Date(now); day7.setDate(day7.getDate()-7);
          const day30 = new Date(now); day30.setDate(day30.getDate()-30);
          const dau = new Set(pageViews.filter(v=>v.user_id && new Date(v.created_at)>day1).map(v=>v.user_id)).size;
          const wau = new Set(pageViews.filter(v=>v.user_id && new Date(v.created_at)>day7).map(v=>v.user_id)).size;
          const mau = new Set(pageViews.filter(v=>v.user_id && new Date(v.created_at)>day30).map(v=>v.user_id)).size;
          const totalViews = pageViews.length;
          const mobileViews = pageViews.filter(v=>v.device==='mobile').length;
          const mobilePct = totalViews > 0 ? Math.round((mobileViews/totalViews)*100) : 0;

          // Pages ranking
          const pageCounts = {};
          pageViews.forEach(v => { pageCounts[v.page] = (pageCounts[v.page]||0)+1; });
          const topPages = Object.entries(pageCounts).sort((a,b)=>b[1]-a[1]);

          // Daily views last 7 days
          const dailyCounts = {};
          for (let i=6;i>=0;i--) { const d=new Date(now); d.setDate(d.getDate()-i); dailyCounts[d.toLocaleDateString('es-MX',{weekday:'short'})]=0; }
          pageViews.filter(v=>new Date(v.created_at)>day7).forEach(v=>{
            const k = new Date(v.created_at).toLocaleDateString('es-MX',{weekday:'short'});
            if (dailyCounts[k]!==undefined) dailyCounts[k]++;
          });
          const maxDaily = Math.max(...Object.values(dailyCounts),1);

          // Funnel
          const landingViews = pageViews.filter(v=>v.page==='landing').length;
          const onboardViews = pageViews.filter(v=>v.page==='onboarding').length;
          const salaViews = new Set(pageViews.filter(v=>v.page==='sala'&&v.user_id).map(v=>v.user_id)).size;
          const funnel = [
            { label:'Visitaron Landing', count:landingViews, color:C.turquesa },
            { label:'Iniciaron onboarding', count:onboardViews, color:C.mango },
            { label:'Entraron a La Sala', count:salaViews, color:C.coral },
            { label:'Membresía de paga', count:paidUsers.length, color:'#534AB7' },
          ];
          const maxFunnel = Math.max(...funnel.map(f=>f.count),1);

          // Inactive users (no page view in 7 days)
          const activeIds = new Set(pageViews.filter(v=>v.user_id&&new Date(v.created_at)>day7).map(v=>v.user_id));
          const inactiveUsers = users.filter(u=>u.id&&!activeIds.has(u.id)&&u.membership!=='admin');

          const askAI = async () => {
            setAiLoading(true);
            try {
              const ctx = `Dashboard T&D:\n- ${totalUsers} usuarios (${paidUsers.length} de paga)\n- MRR: $${mrr}\n- DAU: ${dau}, WAU: ${wau}, MAU: ${mau}\n- ${totalViews} page views (${mobilePct}% móvil)\n- Conversión: ${convRate}%\n- ${inactiveUsers.length} usuarios inactivos\n- Membresías: ${JSON.stringify(tierCounts)}\n- Páginas top: ${topPages.slice(0,5).map(([p,c])=>p+':'+c).join(', ')}`;
              const { data: { session: s1 } } = await supabase.auth.getSession();
              const r = await fetch('/api/chat', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${s1?.access_token}`}, body:JSON.stringify({ messages:[{role:'user',content:`Analiza estos datos de mi plataforma SaaS y dame 5 sugerencias concretas para mejorar:\n${ctx}`}], userContext:{userName:'Bruno (Admin)',businessName:'Thinker & Doer',businessType:'SaaS',membership:'admin'} }) });
              const d = await r.json();
              setAiSuggestions(d.text||'No se pudo generar análisis');
            } catch(e) { setAiSuggestions('Error: '+e.message); }
            setAiLoading(false);
          };

          return (
            <>
              {/* Engagement KPIs */}
              <div style={{ display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(5,1fr)', gap:12, marginBottom:24 }}>
                {[{l:'DAU',v:dau,e:'📊'},{l:'WAU',v:wau,e:'📈'},{l:'MAU',v:mau,e:'📅'},{l:'Page Views',v:totalViews,e:'👁'},{l:'Móvil',v:mobilePct+'%',e:'📱'}].map((k,i)=>(
                  <div key={i} style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:14, textAlign:'center' }}>
                    <p style={{ fontSize:20, margin:'0 0 2px' }}>{k.e}</p>
                    <p style={{ fontSize:20, fontWeight:600, margin:'0 0 2px' }}>{k.v}</p>
                    <p style={{ fontSize:10, color:t.textSecondary, margin:0 }}>{k.l}</p>
                  </div>
                ))}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:16, marginBottom:24 }}>
                {/* Daily views chart */}
                <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.25rem' }}>
                  <p style={{ fontSize:14, fontWeight:500, margin:'0 0 14px' }}>Visitas últimos 7 días</p>
                  <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:100 }}>
                    {Object.entries(dailyCounts).map(([day,count],i)=>(
                      <div key={i} style={{ flex:1, textAlign:'center' }}>
                        <div style={{ height:`${(count/maxDaily)*80}px`, background:C.logoGrad, borderRadius:'4px 4px 0 0', minHeight:2, margin:'0 auto', width:'70%' }}/>
                        <p style={{ fontSize:10, color:t.textSecondary, margin:'4px 0 0' }}>{day}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Funnel */}
                <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.25rem' }}>
                  <p style={{ fontSize:14, fontWeight:500, margin:'0 0 14px' }}>Funnel de conversión</p>
                  {funnel.map((f,i)=>(
                    <div key={i} style={{ marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                        <span style={{ fontSize:12 }}>{f.label}</span>
                        <span style={{ fontSize:12, fontWeight:600 }}>{f.count}</span>
                      </div>
                      <div style={{ height:8, borderRadius:4, background:t.isDark?'#333':'#eee', overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${(f.count/maxFunnel)*100}%`, background:f.color, borderRadius:4 }}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr', gap:16, marginBottom:24 }}>
                {/* Top pages */}
                <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.25rem' }}>
                  <p style={{ fontSize:14, fontWeight:500, margin:'0 0 12px' }}>Páginas más visitadas</p>
                  {topPages.slice(0,8).map(([page,count],i)=>(
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`0.5px solid ${t.border}` }}>
                      <span style={{ fontSize:13 }}>{page}</span>
                      <span style={{ fontSize:13, fontWeight:600, color:C.turquesa }}>{count}</span>
                    </div>
                  ))}
                  {topPages.length===0 && <p style={{ fontSize:12, color:t.textSecondary }}>Sin datos aún</p>}
                </div>

                {/* Inactive users */}
                <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.25rem' }}>
                  <p style={{ fontSize:14, fontWeight:500, margin:'0 0 12px', color:C.coral }}>⚠ Usuarios inactivos ({inactiveUsers.length})</p>
                  {inactiveUsers.slice(0,6).map(u=>(
                    <div key={u.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom:`0.5px solid ${t.border}` }}>
                      <UserAvatar url={u.avatar_url} name={u.full_name} size={24}/>
                      <span style={{ flex:1, fontSize:12 }}>{u.full_name||u.email}</span>
                      <span style={{ fontSize:10, color:t.textSecondary }}>{TIER_LABELS[u.membership]||'Free'}</span>
                    </div>
                  ))}
                  {inactiveUsers.length===0 && <p style={{ fontSize:12, color:C.turquesa }}>✅ Todos activos</p>}
                </div>
              </div>

              {/* AI Suggestions */}
              <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.5rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
                  <p style={{ fontSize:15, fontWeight:500, margin:0 }}>🧠 Análisis con IA</p>
                  <button onClick={askAI} disabled={aiLoading} style={{ background:C.logoGrad, color:'#fff', border:'none', borderRadius:50, padding:'8px 20px', fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:font, opacity:aiLoading?0.6:1 }}>
                    {aiLoading?'Analizando...':'Pedir análisis a Genio'}
                  </button>
                </div>
                {aiSuggestions ? (
                  <div style={{ fontSize:14, lineHeight:1.8, whiteSpace:'pre-wrap' }}>{aiSuggestions}</div>
                ) : (
                  <p style={{ fontSize:13, color:t.textSecondary, margin:0 }}>Presiona el botón para que Genio analice todos tus datos y te dé sugerencias.</p>
                )}
              </div>
            </>
          );
        })()}

        {/* ═══ EVENTS ═══ */}
        {tab === 'events' && (
          <>
            <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.5rem', marginBottom:24 }}>
              <p style={{ fontSize:15, fontWeight:500, margin:'0 0 14px' }}>Crear evento</p>
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
                <input value={evtForm.title} onChange={e=>setEvtForm(f=>({...f,title:e.target.value}))} placeholder="Título del evento" style={inp}/>
                <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr', gap:10 }}>
                  <input type="datetime-local" value={evtForm.event_date} onChange={e=>setEvtForm(f=>({...f,event_date:e.target.value}))} style={inp}/>
                  <select value={evtForm.event_type} onChange={e=>setEvtForm(f=>({...f,event_type:e.target.value}))} style={inp}>
                    <option value="general">🌎 General (todos)</option>
                    <option value="spark">⚡ Spark Seeker</option>
                    <option value="momentum">📊 Momentum Builder</option>
                    {/* <option value="growth">🚀 Growth Mastermind</option> */}
                    <option value="onsite">🏢 Onsite México</option>
                    <option value="especial">✨ Especial</option>
                  </select>
                  <input value={evtForm.meet_link} onChange={e=>setEvtForm(f=>({...f,meet_link:e.target.value}))} placeholder="Link de Google Meet" style={inp}/>
                </div>
                <input value={evtForm.description} onChange={e=>setEvtForm(f=>({...f,description:e.target.value}))} placeholder="Descripción corta" style={inp}/>
              </div>
              <button disabled={evtSaving} onClick={async()=>{if(!evtForm.title||!evtForm.event_date||evtSaving)return;setEvtSaving(true);try{const{error}=await supabase.from('events').insert({...evtForm,event_date:new Date(evtForm.event_date).toISOString()});if(error)console.error('[events] insert error:',error);setEvtForm({title:'',event_date:'',event_type:'general',meet_link:'',description:''});loadEvents()}finally{setEvtSaving(false)}}} style={{ background:evtSaving?'#555':C.logoGrad, color:'#fff', border:'none', borderRadius:50, padding:'10px 28px', fontSize:14, fontWeight:500, cursor:evtSaving?'not-allowed':'pointer', fontFamily:font, opacity:evtSaving?0.6:1 }}>
                {evtSaving?'Creando...':'Crear evento'}
              </button>
            </div>

            <p style={{ fontSize:15, fontWeight:500, margin:'0 0 12px' }}>Eventos ({allEvents.length})</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {allEvents.map(ev => {
                const d = new Date(ev.event_date);
                const typeEmoji = {spark:'⚡',momentum:'📊',growth:'🚀',onsite:'🏢',general:'🌎',especial:'✨'}[ev.event_type]||'📅';
                const typeLabel = {spark:'Spark',momentum:'Momentum',growth:'Growth',onsite:'Onsite',general:'General',especial:'Especial'}[ev.event_type]||ev.event_type;
                return (
                  <div key={ev.id} style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:'14px 18px', display:'flex', alignItems:'center', gap:14 }}>
                    <div style={{ width:50, height:50, borderRadius:12, background:`linear-gradient(135deg, ${C.turquesa}20, ${C.mango}20)`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ fontSize:18, fontWeight:700, lineHeight:1 }}>{d.getDate()}</span>
                      <span style={{ fontSize:9, fontWeight:600, color:t.textSecondary, textTransform:'uppercase' }}>{d.toLocaleDateString('es-MX',{month:'short'})}</span>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:14, fontWeight:500, margin:'0 0 2px' }}>{ev.title}</p>
                      <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>{typeEmoji} {typeLabel} · {d.toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'})} · {ev.meet_link?'🔗 Meet':'Sin link'}</p>
                    </div>
                    <button onClick={async(e)=>{e.target.disabled=true;const{error}=await supabase.from('events').delete().eq('id',ev.id);if(error){console.error('[events] delete error:',error);alert('Error al eliminar: '+error.message);e.target.disabled=false}else{loadEvents()}}} style={{ background:'none', border:'none', color:'#999', fontSize:16, cursor:'pointer' }}>×</button>
                  </div>
                );
              })}
              {allEvents.length===0 && <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:20 }}>No hay eventos programados</p>}
            </div>
          </>
        )}

        {/* ═══ FEEDBACK ═══ */}
        {tab === 'feedback' && (
          <>
            <p style={{ fontSize:15, fontWeight:500, margin:'0 0 16px' }}>Reportes ({feedbackList.length}) · {feedbackList.filter(f=>f.status==='new').length} nuevos</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {feedbackList.map(fb => {
                const sc = {new:C.coral, reviewing:C.mango, resolved:C.turquesa}[fb.status]||'#999';
                const sl = {new:'Nuevo', reviewing:'Revisando', resolved:'Resuelto'}[fb.status]||fb.status;
                const typeEmoji = {bug:'🐛', suggestion:'💡', question:'❓'}[fb.report_type]||'📝';
                return (
                  <div key={fb.id} style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:'14px 18px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:8 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span>{typeEmoji}</span>
                        <span style={{ fontSize:13, fontWeight:500 }}>{fb.user_email}</span>
                        <span style={{ fontSize:10, color:t.textSecondary }}>{fb.device} · {fb.browser}</span>
                      </div>
                      <select value={fb.status} onChange={async e=>{await supabase.from('feedback').update({status:e.target.value}).eq('id',fb.id);setFeedbackList(p=>p.map(f=>f.id===fb.id?{...f,status:e.target.value}:f))}} style={{ fontSize:11, padding:'3px 8px', borderRadius:8, border:`0.5px solid ${t.border}`, background:t.card, color:sc, fontWeight:600, fontFamily:font }}>
                        <option value="new">Nuevo</option>
                        <option value="reviewing">Revisando</option>
                        <option value="resolved">Resuelto</option>
                      </select>
                    </div>
                    <p style={{ fontSize:14, margin:'0 0 6px', lineHeight:1.6 }}>{fb.description}</p>
                    <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>Página: {fb.page_url} · {fb.membership} · {new Date(fb.created_at).toLocaleDateString('es-MX')}</p>
                    {fb.screenshot_url && <a href={fb.screenshot_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:C.turquesa, marginTop:4, display:'inline-block' }}>📎 Ver captura</a>}
                  </div>
                );
              })}
              {feedbackList.length===0 && <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:20 }}>No hay reportes</p>}
            </div>
          </>
        )}

        {/* ═══ PUSH NOTIFICATIONS ═══ */}
        {tab === 'push' && (
          <>
            <p style={{ fontSize:15, fontWeight:500, margin:'0 0 16px' }}>Enviar notificacion</p>
            <div style={{ display:'flex', flexDirection:'column', gap:14, maxWidth:480 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:500, display:'block', marginBottom:4 }}>Tipo</label>
                <select value={pushTipo} onChange={e=>{
                  const v=e.target.value; setPushTipo(v); setPushResult(null);
                  if(v==='noticias'){setPushTitle('Nuevas noticias');setPushBody('Hay novedades en la comunidad. Entra a ver!');}
                  else if(v==='sesion'){setPushTitle('Tu sesion es manana');setPushBody('Recuerda que manana tienes sesion. Te esperamos!');}
                  else if(v==='ventas'){setPushTitle('No olvides registrar ventas');setPushBody('Registra tus ventas de hoy para mantener tu racha.');}
                  else{setPushTitle('');setPushBody('');}
                }} style={{ width:'100%', padding:'9px 12px', border:`0.5px solid ${t.border}`, borderRadius:10, fontSize:13, fontFamily:font, background:t.card, color:t.text }}>
                  <option value="noticias">Nuevas noticias</option>
                  <option value="sesion">Tu sesion es manana</option>
                  <option value="ventas">No olvides registrar ventas</option>
                  <option value="personalizado">Personalizado</option>
                </select>
              </div>
              {pushTipo==='personalizado' && (
                <>
                  <div>
                    <label style={{ fontSize:12, fontWeight:500, display:'block', marginBottom:4 }}>Titulo</label>
                    <input value={pushTitle} onChange={e=>setPushTitle(e.target.value)} placeholder="Titulo de la notificacion" style={{ width:'100%', padding:'9px 12px', border:`0.5px solid ${t.border}`, borderRadius:10, fontSize:13, fontFamily:font, background:t.card, color:t.text, boxSizing:'border-box' }}/>
                  </div>
                  <div>
                    <label style={{ fontSize:12, fontWeight:500, display:'block', marginBottom:4 }}>Mensaje</label>
                    <textarea value={pushBody} onChange={e=>setPushBody(e.target.value)} rows={3} placeholder="Cuerpo del mensaje" style={{ width:'100%', padding:'9px 12px', border:`0.5px solid ${t.border}`, borderRadius:10, fontSize:13, fontFamily:font, background:t.card, color:t.text, resize:'vertical', boxSizing:'border-box' }}/>
                  </div>
                </>
              )}
              <div>
                <label style={{ fontSize:12, fontWeight:500, display:'block', marginBottom:4 }}>Destinatarios</label>
                <select value={pushDest} onChange={e=>{setPushDest(e.target.value);setPushResult(null)}} style={{ width:'100%', padding:'9px 12px', border:`0.5px solid ${t.border}`, borderRadius:10, fontSize:13, fontFamily:font, background:t.card, color:t.text }}>
                  <option value="all">Todos</option>
                  <option value="spark_seeker">Spark</option>
                  <option value="momentum_builder">Momentum</option>
                  {/* <option value="growth_mastermind">Growth</option> */}
                  <option value="onsite_mexico">Onsite</option>
                </select>
              </div>
              <button onClick={async()=>{
                if(!pushTitle||!pushBody){setPushResult({error:'Titulo y mensaje requeridos'});return;}
                setPushSending(true);setPushResult(null);
                try{
                  const userIds = pushDest==='all'?[]:users.filter(u=>u.membership===pushDest).map(u=>u.id);
                  const { data: { session: notifSession } } = await supabase.auth.getSession();
                  const res = await fetch('/api/send-notification',{method:'POST',headers:{'Content-Type':'application/json','Authorization':`Bearer ${notifSession?.access_token}`},body:JSON.stringify({user_ids:userIds,title:pushTitle,body:pushBody,url:'/sala'})});
                  const data = await res.json();
                  if(res.ok) setPushResult({ok:true,...data});
                  else setPushResult({error:data.error||'Error al enviar'});
                }catch(err){setPushResult({error:err.message});}
                setPushSending(false);
              }} disabled={pushSending} style={{ padding:'11px 24px', borderRadius:12, border:'none', background:C.turquesa, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:font, opacity:pushSending?0.6:1 }}>
                {pushSending?'Enviando...':'Enviar notificacion'}
              </button>
              {pushResult && (
                <div style={{ padding:'12px 16px', borderRadius:12, background:pushResult.error?'#eb636b15':C.turquesa+'15', border:`0.5px solid ${pushResult.error?C.coral:C.turquesa}`, fontSize:13 }}>
                  {pushResult.error
                    ? <span style={{ color:C.coral }}>{pushResult.error}</span>
                    : <span style={{ color:C.turquesa }}>Enviadas: {pushResult.success}/{pushResult.total}{pushResult.cleaned?' · '+pushResult.cleaned+' expiradas limpiadas':''}</span>
                  }
                </div>
              )}
            </div>
          </>
        )}
        {/* ═══ TU RADAR ═══ */}
        {tab === 'tu_radar_admin' && (() => {
          const radarUsers = users.filter(u => u.membership === 'tu_radar');
          return (
            <>
              <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)', gap:12, marginBottom:24 }}>
                <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:16, textAlign:'center' }}>
                  <p style={{ fontSize:28, fontWeight:600, margin:'0 0 2px' }}>{radarUsers.length}</p>
                  <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>Alumnos total</p>
                </div>
                <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:16, textAlign:'center' }}>
                  <p style={{ fontSize:28, fontWeight:600, margin:'0 0 2px', color:C.turquesa }}>${radarUsers.length * 59}</p>
                  <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>Ingresos Tu Radar</p>
                </div>
                <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:16, padding:16, textAlign:'center' }}>
                  <p style={{ fontSize:28, fontWeight:600, margin:'0 0 2px' }}>🧭</p>
                  <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>Tu Radar</p>
                </div>
              </div>

              <div style={{ background:t.card, border:`0.5px solid ${t.border}`, borderRadius:20, padding:'1.5rem' }}>
                <p style={{ fontSize:15, fontWeight:500, margin:'0 0 16px' }}>Alumnos de Tu Radar</p>
                {radarUsers.length === 0 ? (
                  <p style={{ fontSize:13, color:t.textSecondary, textAlign:'center', padding:20 }}>Aún no hay alumnos. Cuando alguien compre la Tu Radar aparecerá aquí.</p>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {radarUsers.map(u => (
                      <div key={u.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:14, border:`0.5px solid ${t.border}`, flexWrap:'wrap' }}>
                        <UserAvatar url={u.avatar_url} name={u.full_name} size={36} />
                        <div style={{ flex:1, minWidth:120 }}>
                          <p style={{ fontSize:14, fontWeight:500, margin:0 }}>{u.full_name || 'Sin nombre'}</p>
                          <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>{u.email}</p>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <p style={{ fontSize:12, color:t.textSecondary, margin:0 }}>{u.city || ''}</p>
                          <p style={{ fontSize:11, color:t.textSecondary, margin:0 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString('es') : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </div>

      {/* MONTHLY REPORT MODAL */}
      {showMonthlyReport && (() => {
        const now = new Date();
        const monthName = now.toLocaleString('es', { month: 'long', year: 'numeric' });
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const newThisMonth = users.filter(u => u.created_at && new Date(u.created_at) >= thisMonth).length;
        const newLastMonth = users.filter(u => u.created_at && new Date(u.created_at) >= prevMonth && new Date(u.created_at) < thisMonth).length;
        const activeThisMonth = new Set(pageViews.filter(v => v.user_id && new Date(v.created_at) >= thisMonth).map(v => v.user_id)).size;
        const activeIds = new Set(pageViews.filter(v => v.user_id && new Date(v.created_at) >= thisMonth).map(v => v.user_id));
        const inactiveThisMonth = users.filter(u => u.id && !activeIds.has(u.id) && u.membership !== 'admin').length;
        const chatThisMonth = chatMsgs.filter(m => new Date(m.created_at) >= thisMonth).length;
        const newsThisMonth = newsletters.filter(n => new Date(n.created_at) >= thisMonth).length;
        const prevMRR = mrr * 0.9;
        const mrrGrowth = prevMRR > 0 ? Math.round(((mrr - prevMRR) / prevMRR) * 100) : 0;
        const topActive = Object.entries(pageViews.filter(v => v.user_id && new Date(v.created_at) >= thisMonth).reduce((a, v) => { a[v.user_id] = (a[v.user_id] || 0) + 1; return a; }, {})).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([uid, count]) => { const u = users.find(x => x.id === uid); return { name: u?.full_name || 'Unknown', count, membership: u?.membership }; });
        const tierColors = { free_observer: '#999', doer: C.mango, spark_seeker: C.mango, momentum_builder: C.turquesa, growth_mastermind: '#534AB7', onsite_mexico: C.coral, admin: '#333' };
        const tierLabels = { free_observer: 'Free', doer: 'Doer', spark_seeker: 'Doer', momentum_builder: 'Momentum', growth_mastermind: 'Growth', onsite_mexico: 'Onsite', admin: 'Admin' };
        const dismissKey = `td_admin_report_${now.getFullYear()}_${now.getMonth()}`;
        const dismiss = () => { localStorage.setItem(dismissKey, '1'); setShowMonthlyReport(false); };

        const askReportAI = async () => {
          setReportAILoading(true);
          try {
            const ctx = `Reporte mensual T&D (${monthName}):\n- Total usuarios: ${totalUsers} (${newThisMonth} nuevos este mes, ${newLastMonth} el mes pasado)\n- MRR: $${mrr} USD (crecimiento: ${mrrGrowth}%)\n- Usuarios activos este mes: ${activeThisMonth}\n- Usuarios inactivos: ${inactiveThisMonth}\n- Membresías: ${JSON.stringify(tierCounts)}\n- Tasa de conversión free→paga: ${convRate}%\n- Chat mensajes este mes: ${chatThisMonth}\n- Newsletters este mes: ${newsThisMonth}\n- Top usuarios: ${topActive.map(u => u.name + ' (' + u.count + ' visitas)').join(', ')}`;
            const { data: { session: s2 } } = await supabase.auth.getSession();
            const r = await fetch('/api/chat', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s2?.access_token}` }, body: JSON.stringify({ messages: [{ role: 'user', content: `Eres un consultor SaaS experto. Analiza este reporte mensual y dame 5 sugerencias estratégicas concretas para crecer:\n${ctx}` }], userContext: { userName: 'Bruno (Admin)', businessName: 'Thinker & Doer', businessType: 'SaaS', membership: 'admin' } }) });
            const d = await r.json();
            setReportAI(d.text || 'No se pudo generar análisis');
          } catch (e) { setReportAI('Error: ' + e.message); }
          setReportAILoading(false);
        };

        return (
          <>
            <div onClick={dismiss} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9998 }} />
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'calc(100vw - 2rem)', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', zIndex: 9999, background: t.card, borderRadius: 24, padding: isMobile ? '1.5rem' : '2rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', borderTop: '3px solid', borderImage: 'linear-gradient(135deg, #5dc1b9, #FDBE02, #eb636b) 1' }}>

              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <p style={{ fontSize: 28, margin: '0 0 8px' }}>📊</p>
                <p style={{ fontFamily: serif, fontSize: 22, fontWeight: 400, margin: '0 0 4px' }}>Reporte mensual</p>
                <p style={{ fontSize: 14, color: t.textSecondary, margin: 0, textTransform: 'capitalize' }}>{monthName}</p>
              </div>

              {/* KPIs grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
                {[
                  { l: 'Nuevos este mes', v: newThisMonth, sub: newLastMonth > 0 ? `vs ${newLastMonth} mes pasado` : null, color: newThisMonth > newLastMonth ? C.turquesa : C.coral },
                  { l: 'MRR', v: `$${mrr}`, sub: `${mrrGrowth >= 0 ? '+' : ''}${mrrGrowth}%`, color: mrrGrowth >= 0 ? C.turquesa : C.coral },
                  { l: 'Conversión', v: `${convRate}%`, sub: 'free → paga' },
                  { l: 'Activos', v: activeThisMonth, sub: 'este mes' },
                  { l: 'Inactivos', v: inactiveThisMonth, sub: 'no entraron', color: C.coral },
                  { l: 'Total', v: totalUsers, sub: 'usuarios' },
                ].map((k, i) => (
                  <div key={i} style={{ background: t.isDark ? '#222' : '#f8f8f6', borderRadius: 14, padding: '12px 10px', textAlign: 'center' }}>
                    <p style={{ fontSize: 22, fontWeight: 600, margin: '0 0 2px', color: k.color || t.text }}>{k.v}</p>
                    <p style={{ fontSize: 11, color: t.textSecondary, margin: 0 }}>{k.l}</p>
                    {k.sub && <p style={{ fontSize: 10, color: k.color || t.textSecondary, margin: '2px 0 0' }}>{k.sub}</p>}
                  </div>
                ))}
              </div>

              {/* Membership distribution */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 8px' }}>Distribución de membresías</p>
                <div style={{ display: 'flex', height: 24, borderRadius: 12, overflow: 'hidden', marginBottom: 6 }}>
                  {Object.entries(tierCounts).filter(([, c]) => c > 0).map(([tier, count]) => (
                    <div key={tier} style={{ flex: count, background: tierColors[tier] || '#999', transition: 'flex 0.3s' }} title={`${tierLabels[tier] || tier}: ${count}`} />
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(tierCounts).filter(([, c]) => c > 0).map(([tier, count]) => (
                    <span key={tier} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: tierColors[tier] || '#999' }} />
                      {tierLabels[tier] || tier}: {count}
                    </span>
                  ))}
                </div>
              </div>

              {/* Activity */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                <div style={{ background: t.isDark ? '#222' : '#f8f8f6', borderRadius: 14, padding: 12 }}>
                  <p style={{ fontSize: 11, color: t.textSecondary, margin: '0 0 2px' }}>💬 Chat</p>
                  <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{chatThisMonth} msgs</p>
                </div>
                <div style={{ background: t.isDark ? '#222' : '#f8f8f6', borderRadius: 14, padding: 12 }}>
                  <p style={{ fontSize: 11, color: t.textSecondary, margin: '0 0 2px' }}>📰 Noticias</p>
                  <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{newsThisMonth}</p>
                </div>
              </div>

              {/* Top 3 active */}
              {topActive.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 8px' }}>🏆 Top usuarios más activos</p>
                  {topActive.map((u, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: `0.5px solid ${t.border}` }}>
                      <span style={{ fontSize: 16 }}>{['🥇', '🥈', '🥉'][i]}</span>
                      <span style={{ flex: 1, fontSize: 13 }}>{u.name}</span>
                      <span style={{ fontSize: 11, color: tierColors[u.membership] || '#999' }}>{tierLabels[u.membership] || ''}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.turquesa }}>{u.count} visitas</span>
                    </div>
                  ))}
                </div>
              )}

              {/* AI Analysis */}
              <div style={{ background: t.isDark ? '#222' : '#f8f8f6', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                {reportAI ? (
                  <div style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{reportAI}</div>
                ) : (
                  <button onClick={askReportAI} disabled={reportAILoading} style={{ width: '100%', background: C.logoGrad, color: '#fff', border: 'none', borderRadius: 50, padding: '12px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: font, opacity: reportAILoading ? 0.6 : 1 }}>
                    {reportAILoading ? '🧠 Analizando...' : '🧠 Pedir análisis IA'}
                  </button>
                )}
              </div>

              <button onClick={dismiss} style={{ width: '100%', background: 'none', border: `0.5px solid ${t.border}`, borderRadius: 50, padding: '12px', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: font, color: t.textSecondary }}>Cerrar reporte</button>
            </div>
          </>
        );
      })()}
    </div>
  );
}
